import { useState, useEffect, useRef } from "react";
import { ToggleLeft, ToggleRight } from "lucide-react";
import { YouTubePlayerRef } from "./YouTubeEmbed";

interface TranscriptItem {
  text: string;
  start: number;
  duration: number;
}

interface YouTubeChapter {
  title: string;
  timestamp: string;
  start_time: number; // start time in seconds
}

interface TranscriptSection {
  title: string;
  timestamp: string;
  description: string;
  startTime: number; // start time in seconds (from YouTubeTranscript component)
}

interface UnifiedItem {
  type: 'chapter' | 'transcript';
  start: number;
  title?: string;
  text?: string;
  timestamp: string;
  isChapterHeader?: boolean;
}

interface UnifiedTranscriptProps {
  transcript: TranscriptItem[];
  chapters: YouTubeChapter[];
  sections: TranscriptSection[];
  hasChapters: boolean;
  playerRef?: React.RefObject<YouTubePlayerRef>;
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Parse timestamp string (e.g., "1:23") to seconds
const parseTimestamp = (timestamp: string): number => {
  const parts = timestamp.split(':').map(Number);
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return 0;
};

// No additional grouping needed - transcript items are already 30-second chunks

export const UnifiedTranscript = ({ 
  transcript, 
  chapters, 
  sections, 
  hasChapters, 
  playerRef 
}: UnifiedTranscriptProps) => {
  const [autoScroll, setAutoScroll] = useState(false);
  const [unifiedItems, setUnifiedItems] = useState<UnifiedItem[]>([]);

  useEffect(() => {
    // Process and combine chapters and transcripts
    const processData = () => {
      const items: UnifiedItem[] = [];
      
      // Get chapters (real or generated sections)
      const chaptersToUse = hasChapters && chapters.length > 0 ? chapters : sections;
      
      // Get chapter start times (use actual numeric fields instead of parsing)
      const parsedChapters = chaptersToUse.map(chapter => ({
        ...chapter,
        start: 'start_time' in chapter ? chapter.start_time : chapter.startTime
      })).sort((a, b) => a.start - b.start);
      
      // Use transcript items directly (they're already 30-second chunks)
      const sortedTranscript = transcript.sort((a, b) => a.start - b.start);
      
      // Combine chapters and transcripts chronologically
      let chapterIndex = 0;
      let transcriptIndex = 0;
      
      while (chapterIndex < parsedChapters.length || transcriptIndex < sortedTranscript.length) {
        const currentChapter = parsedChapters[chapterIndex];
        const currentTranscript = sortedTranscript[transcriptIndex];
        
        // Decide whether to add chapter or transcript next
        if (
          currentChapter && 
          (!currentTranscript || currentChapter.start <= currentTranscript.start)
        ) {
          // Add chapter
          items.push({
            type: 'chapter',
            start: currentChapter.start,
            title: currentChapter.title,
            timestamp: currentChapter.timestamp,
            isChapterHeader: true
          });
          chapterIndex++;
        } else if (currentTranscript) {
          // Add transcript
          items.push({
            type: 'transcript',
            start: currentTranscript.start,
            text: currentTranscript.text,
            timestamp: formatTime(currentTranscript.start)
          });
          transcriptIndex++;
        } else {
          break;
        }
      }
      
      setUnifiedItems(items);
    };
    
    processData();
  }, [transcript, chapters, sections, hasChapters]);

  const handleSeek = (seconds: number) => {
    if (playerRef?.current) {
      playerRef.current.seekTo(seconds);
    }
  };

  // Show empty state if no content is available
  if ((!transcript || transcript.length === 0) && (!chapters || chapters.length === 0) && (!sections || sections.length === 0)) {
    return (
      <div className="h-full flex flex-col bg-gray-50" style={{ fontFamily: 'Lexend Deca' }}>
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 bg-white">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm font-medium text-gray-900">Transcript & Chapters</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-6 max-w-sm mx-auto">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <h4 className="text-base font-medium text-gray-700 mb-2">No Transcript Available</h4>
            <p className="text-sm text-gray-500 mb-1">This video doesn't have captions or transcripts available.</p>
            <p className="text-xs text-gray-400">You can still watch and study from the video above.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" style={{ fontFamily: 'Lexend Deca' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
          <span className="text-sm font-medium text-gray-900">Transcript & Chapters</span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            {autoScroll ? (
              <ToggleRight className="w-5 h-5 text-gray-700" />
            ) : (
              <ToggleLeft className="w-5 h-5" />
            )}
            Auto Scroll
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {unifiedItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="max-w-sm mx-auto">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p className="text-sm text-gray-500">No transcript or chapters available for this video</p>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-6">
            {unifiedItems.map((item, index) => (
              <div key={index}>
                {item.type === 'chapter' ? (
                  // Chapter Header - Document style heading
                  <div
                    onClick={() => handleSeek(item.start)}
                    className="cursor-pointer group mb-4"
                  >
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="text-xs text-gray-400 font-mono">{item.timestamp}</span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 leading-relaxed group-hover:text-gray-700 transition-colors">
                      {item.title}
                    </h2>
                  </div>
                ) : (
                  // Transcript Entry - Document style paragraph
                  <div
                    onClick={() => handleSeek(item.start)}
                    className="cursor-pointer group"
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-xs text-gray-400 font-mono pt-1 flex-shrink-0 w-12">
                        {item.timestamp}
                      </span>
                      <p className="text-base text-gray-800 leading-relaxed group-hover:text-gray-600 transition-colors">
                        {item.text}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
